import { FC, useMemo } from 'react';
import { ZodType } from 'zod';

import { CreateVoucherBatchFormData } from '@/api/voucher/voucher.types.ts';
import purchaseIcon from '@/assets/purchaseIconGreen.png';
import CustomConfirmationMessageModal from '@/components/controls/CustomConfirmationMessageModal';
import { ComboboxOption } from '@/components/controls/CustomDropdown';
import CustomEntityForm from '@/components/controls/CustomEntityForm';
import { EntityFormFieldSpecification } from '@/components/controls/CustomEntityFormField';
import { CreateVoucherBatchSchema } from '@/components/zod/voucher.types';
import { usePurchaseVoucher } from '@/hooks/usePurchaseVouchers.ts';

const PurchaseVouchersForm: FC = () => {
  const {
    golfCourses,
    rewardTiers,
    isLoading,
    showConfirmQtyModal,
    confirmQtyText,
    handleSubmit,
    handleConfirm,
    handleCancel,
    handleGolfCourseChange,
  } = usePurchaseVoucher();

  const golfCourseOptions: ComboboxOption[] = useMemo(
    () =>
      (golfCourses || [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((gc) => ({
          label: gc.name,
          value: gc.id,
        })),
    [golfCourses]
  );

  const rewardTierOptions: ComboboxOption[] = useMemo(
    () =>
      (rewardTiers || []).map((tier) => ({
        label: `${tier.name} (R${tier.prize.toLocaleString()})`,
        value: tier.id,
      })),
    [rewardTiers]
  );

  const fields: EntityFormFieldSpecification[] = [
    {
      name: 'voucherName',
      label: 'Event Name',
      fieldType: 'input',
      type: 'text',
      placeholder: 'Enter Event Name',
      validation: { required: true },
    },
    {
      name: 'golfCourseId',
      label: 'Golf Course',
      fieldType: 'select',
      placeholder: 'Select a Golf Course',
      validation: { required: true },
      selectOptions: golfCourseOptions,
      onChange: handleGolfCourseChange,
    },
    {
      name: 'rewardTierId',
      label: 'Reward Tier',
      fieldType: 'select',
      placeholder: 'Select Reward Tier',
      validation: { required: true },
      selectOptions: rewardTierOptions,
    },
    {
      name: 'validFor',
      label: 'Event Date',
      fieldType: 'input',
      type: 'date',
      placeholder: 'YYYY-MM-DD',
      validation: { required: true },
    },
    {
      name: 'voucherCount',
      label: 'Quantity',
      fieldType: 'input',
      type: 'number',
      placeholder: 'Enter Quantity',
      validation: { required: true },
    },
  ];

  return (
    <>
      <CustomEntityForm<CreateVoucherBatchFormData>
        schema={CreateVoucherBatchSchema as ZodType<CreateVoucherBatchFormData>}
        fields={fields}
        initialMode="create"
        isLoading={isLoading}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        title={
          <div className="flex items-center justify-center md:justify-start gap-2">
            <img
              src={purchaseIcon}
              alt="Invite icon"
              className="w-6 h-6 object-contain"
            />
            <span>Purchase Vouchers</span>
          </div>
        }
        submitButtonText="Purchase"
        entityData={{
          voucherName: '',
          golfCourseId: '',
          validFor: '',
          voucherCount: 1,
          rewardTierId: '',
          type: 'corporate',
        }}
      />

      <CustomConfirmationMessageModal
        isOpen={showConfirmQtyModal}
        title="Confirm your purchase"
        message={confirmQtyText}
        onCancel={handleCancel}
        cancelButtonText="Cancel"
        onConfirm={handleConfirm}
        confirmButtonText="Confirm"
      />
    </>
  );
};

export default PurchaseVouchersForm;
